import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { api, formatErr } from "@/lib/api";

const Shell = ({ title, text, children, testId }) => (
  <div className="grid min-h-screen place-items-center bg-[#F5F5F7] px-6 py-10">
    <div className="panel w-full max-w-sm space-y-4 p-8" data-testid={testId}>
      <Logo size={28} />
      <Link to="/login" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-black" data-testid="reset-back-login"><ArrowLeft className="h-4 w-4" />Torna al login</Link>
      <div><h1 className="text-2xl font-semibold">{title}</h1><p className="mt-2 text-sm text-zinc-500">{text}</p></div>
      {children}
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  if (sent) return <Shell testId="forgot-sent" title="Controlla la tua email" text={`Se ${email} corrisponde a un account attivo, riceverai a breve un link per scegliere una nuova password. Il link vale 1 ora. Controlla anche la cartella spam.`} />;
  return (
    <Shell testId="forgot-page" title="Password dimenticata" text="Inserisci l'email con cui accedi: ti invieremo un link per sceglierne una nuova.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label className="text-xs">Email</Label><Input type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" data-testid="forgot-email" /></div>
        <Button type="submit" disabled={busy} className="h-12 w-full" data-testid="forgot-submit">{busy ? "Invio…" : "Invia link"}</Button>
      </form>
    </Shell>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (p1.length < 8) return setErr("La password deve avere almeno 8 caratteri");
    if (p1 !== p2) return setErr("Le due password non coincidono");
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/reset-password", { token, new_password: p1 });
      toast.success("Password aggiornata: ora puoi accedere");
      nav("/login", { replace: true });
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell testId="reset-page" title="Nuova password" text="Scegli una nuova password per la tua area SALVILO.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label className="text-xs">Nuova password (almeno 8 caratteri)</Label><Input type="password" autoComplete="new-password" required value={p1} onChange={(e) => setP1(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" data-testid="reset-new" /></div>
        <div><Label className="text-xs">Ripeti la nuova password</Label><Input type="password" autoComplete="new-password" required value={p2} onChange={(e) => setP2(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" data-testid="reset-confirm" /></div>
        {err && <p className="text-sm text-red-600" data-testid="reset-error">{err} {err.includes("scaduto") && <Link to="/password-dimenticata" className="underline">Richiedi nuovo link</Link>}</p>}
        <Button type="submit" disabled={busy} className="h-12 w-full" data-testid="reset-submit">{busy ? "Salvataggio…" : "Salva password"}</Button>
      </form>
    </Shell>
  );
}
